import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, InputField } from "../../components/common";
import { useToast } from "../../contexts/ToastContext";
import { useAuth } from "../../hooks/useAuth";

const AdminSignIn: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const { login, user, isAuthenticated } = useAuth();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  // Redirect if already authenticated as admin
  if (isAuthenticated && user?.role === "ADMIN") {
    return <Navigate to="/admin" replace />;
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const loggedInUser = await login({ email, password });

      if (loggedInUser.role !== "ADMIN") {
        showError("Access denied. Admin privileges required.");
        return;
      }
      showSuccess("Welcome back to the admin portal!");
      navigate("/admin");
    } catch (error: any) {
      showError(error.message || "Sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-cyan-600 rounded-lg flex items-center justify-center mb-6">
            <svg
              className="h-8 w-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            RAAH Admin Portal
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to access the administration dashboard
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <InputField
                id="email"
                name="email"
                type="email"
                label="Email Address"
                placeholder="admin@raah.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                autoComplete="email"
                required
              />
            </div>

            <div>
              <InputField
                id="password"
                name="password"
                type="password"
                label="Password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                autoComplete="current-password"
                required
              />
            </div>

            <div>
              <Button
                type="submit"
                fullWidth
                isLoading={loading}
                disabled={loading}
              >
                {loading ? "Signing In..." : "Sign In"}
              </Button>
            </div>
          </form>

          {/* Default Admin Credentials Info */}
          {/* <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 mb-2">
              Default Admin Credentials:
            </h4>
            <div className="text-sm text-blue-700">
              <p>
                <strong>Email:</strong> admin@raah.com
              </p>
              <p>
                <strong>Password:</strong> admin123 (Please change this after first login)
              </p>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              ⚠️ Please change the password after first login for security.
            </p>
          </div> */}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact the development team for support.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-2 text-sm text-cyan-600 hover:text-cyan-700 transition-colors"
          >
            ← Back to main site
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSignIn;
